/**
 * Educational / SEO content for the Numerology hub (not the report generator).
 * Tone: calm, editorial — Astral Moon Magic. (Numerology vertical is noindex.)
 */

import { NUMEROLOGY_COURSE_EXTRA_ARTICLES } from './numerologyContentExtras';
import { NUMEROLOGY_COURSE } from './numerologyCourse';

const ctaNote =
  'Each lesson teaches method and general meaning. A generated report applies the same rules to your birth date and name, then weaves them into a written synthesis. Follow the modules in order; the report uses the same structure with timing and practical insight in one document.';

const ctaCourse =
  'This lesson is part of the structured course. It teaches method and interpretation — separate from the optional personal report product.';

export const NUMEROLOGY_ARTICLES = {
  'm1-what-is-numerology': {
    title: 'What is numerology',
    section: 'about',
    seo: ['What is numerology', 'How numerology works'],
    intro:
      'Numerology is not a verdict on your character. It is a way of turning birth data and names into a small set of repeating themes — emphasis, pace, friction, and skill — so you can talk about your life with more precision than “I’m just like this.” Single digits and a few master numbers are the shared vocabulary; the chart shows where that vocabulary shows up.',
    blocks: [
      {
        h2: 'Core idea',
        p: [
          'In this course, numerology means: use one letter-to-number table and consistent reduction to turn a birth date and birth name into digits that sit in named positions (Life Path, Expression, Soul Urge, Personality, timing, and optional secondary positions). You read each digit in its office — not as a personality label floating in space.',
          'The method is checkable: if your hand calculation disagrees with software, you can line up steps and find the error. Interpretation starts after arithmetic is stable.',
        ],
      },
      {
        h2: 'Why numerology compresses life into digits',
        p: [
          'Experience is messy; numerology deliberately compresses it. Dates and names become sums, sums become 1–9 (or 11, 22, 33 where rules allow), and those digits become interpretive handles. The point of the compression is not to shrink you — it is to make pattern visible: what keeps returning, what asks for practice, what contradicts itself in productive ways.',
          'A digit is never the whole story. It is a theme that has to live inside a position — Life Path versus name versus timing. Same digit, different job: that distinction is what keeps reading honest and stops numerology from turning into a list of adjectives with your birthday on it.',
        ],
      },
      {
        h2: 'In the wider chart',
        p: [
          'Life Path describes long-arc pacing; name numbers describe how you operate, what you crave, and what people meet first; timing layers seasonal emphasis on top. Later lessons teach each position; the skill is holding them in one conversation instead of isolating favorites.',
        ],
      },
      {
        h2: 'Common misreadings',
        ul: [
          'Treating one digit as “who you are” while ignoring the rest of the chart.',
          'Mixing systems (different letter tables or different master rules) and then arguing about meaning.',
          'Skipping arithmetic and jumping to keywords — errors in the date or name always masquerade as “deep” disagreements.',
        ],
      },
      {
        h2: 'Practice',
        p: [
          'In one sentence each, describe what changes if the same digit appeared as Life Path versus as Expression (you do not need real data yet — use digit 5 as a hypothetical).',
        ],
      },
      {
        h2: 'Next',
        p: [
          'Continue with **How numerology works** for the concrete pipeline from date and name to chart positions, then **Core numbers overview** for how the main lenses fit together.',
        ],
      },
    ],
    howInReport:
      'After you enter your date and name, the engine applies the same rules described here and then writes across positions: where themes agree, where they pull apart, and what that tension tends to ask for in real life. That is integrated interpretation — not a single-number label and not a generic essay.',
    ctaNote,
  },

  'numerology-numbers-1-9': {
    title: 'Digits 1–9: meanings, shadow, and interaction',
    section: 'about',
    seo: ['Numerology chart meaning'],
    intro:
      'In readings, digits 1–9 are **shared vocabulary**, not identities. Nobody “is a 6.” A chart asks: where does the **theme of six** show up — long arc (Life Path), outer style (Expression), inner appetite (Soul Urge), first impression (Personality), this year’s weather (Personal Year), or elsewhere? Same digit, different **office**: the office sets the question; the digit colours the answer. A generated report uses these themes to describe **how positions reinforce, contradict, or sequence** — synthesis, not a list of adjectives with your birthday on it. Masters 11, 22, 33 widen the stakes for the related single digit; they have their own lesson. Below is a **reference-level** pass through 1–9: core theme, strengths, shadow, dominance, absence, interaction with other digits, and where the idea usually appears in written output.',
    blocks: [
      {
        h2: 'Where each digit is allowed to speak',
        ul: [
          '**Life Path** — slow storyline: recurring chapters, pace, what keeps returning over years.',
          '**Expression (Destiny)** — whole name: how you tend to execute, perform competence, move in the world.',
          '**Soul Urge** — vowels: private hunger — what you want when you are honest.',
          '**Personality** — consonants: opening handshake — pace, guardrails, surface tone.',
          '**Birthday** — day of month: accent, gift, extra texture on the core story.',
          '**Timing** — Personal Year / Month / Day: seasonal emphasis on top of the chart, not a replacement for core positions.',
        ],
      },
      {
        h2: 'Numbers 1–9 — reference',
        p: [
          'Read **shadow** as the same energy under strain — not moral failure. **Dominant** means the digit appears in several core places or repeats as a family pattern. **Missing** in the name often parallels karmic-lesson language (skills built through practice); keep wording constructive, not deficit-based.',
        ],
        sections: [
          {
            h3: 'Number 1',
            p: [
              '**Core theme:** Initiation — naming reality, taking the first step, owning direction when something must be started.',
              '**Strengths:** Courage to lead without a script, honest self-authorship, clarity when others hesitate.',
              '**Challenges / shadow:** Hurry, brittle independence, difficulty delegating or receiving help, confusing decisiveness with being right.',
              '**When dominant in a chart:** Several cores or strong repetition — pressure to decide, carry visibility, or “go first” in multiple life areas.',
              '**When missing or quiet:** Easier to wait for permission; growth edge is clean self-assertion without collapse into performance.',
              '**How it interacts with other digits:** With **2** — lead versus listen; with **4** — spark versus scaffold; with **8** — personal initiative versus institutional power.',
              '**Where it appears in reports:** Summary and **core numbers** blocks; **patterns / tension** when initiation clashes with cooperation or structure; timing as a season to act.',
            ],
          },
          {
            h3: 'Number 2',
            p: [
              '**Core theme:** Attunement — reading the room, pairing, letting timing and nuance matter.',
              '**Strengths:** Diplomacy, patience, emotional intelligence, genuine cooperation without erasing truth.',
              '**Challenges / shadow:** Absorbed boundaries, swallowed needs, over-reading signals, resentment when harmony is one-sided.',
              '**When dominant in a chart:** Relationship, support, and sensitivity show up as recurring jobs across positions.',
              '**When missing or quiet:** Directness may come easier than sustained attunement; practice naming needs early.',
              '**How it interacts with other digits:** With **1** — partnership versus solo charge; with **7** — feeling versus analysis; with **8** — soft power versus hard leverage.',
              '**Where it appears in reports:** Core synthesis; **tension** when inner urge (e.g. 1 or 8) outruns relational pace; life-area language around care and negotiation.',
            ],
          },
          {
            h3: 'Number 3',
            p: [
              '**Core theme:** Expression — giving ideas shape, voice, and connection; making life speakable or visible.',
              '**Strengths:** Creativity, humour, teaching energy, social warmth, honest articulation of experience.',
              '**Challenges / shadow:** Scatter, image management, talking past depth, anxiety when there is no audience.',
              '**When dominant in a chart:** Visibility, messaging, and creative output keep returning as themes.',
              '**When missing or quiet:** Depth without display may feel safer; growth edge is proportionate sharing and follow-through.',
              '**How it interacts with other digits:** With **4** — air versus structure; with **5** — two kinds of movement (story versus stimulus); with **7** — output versus retreat.',
              '**Where it appears in reports:** Core blocks and **life areas** touching work or self-presentation; patterns when expression overloads stamina.',
            ],
          },
          {
            h3: 'Number 4',
            p: [
              '**Core theme:** Structure — building what lasts, honouring repetition, earning stability through effort.',
              '**Strengths:** Discipline, craft, reliability, calm under long horizons.',
              '**Challenges / shadow:** Rigidity, fear of change, moralising order, confusing comfort with truth.',
              '**When dominant in a chart:** Systems, duty, and endurance show up across several positions.',
              '**When missing or quiet:** Adaptability may outpace follow-through; growth edge is sustainable containers, not only ideas.',
              '**How it interacts with other digits:** With **5** — security versus freedom; with **3** — form versus flow; with **9** — maintenance versus letting go.',
              '**Where it appears in reports:** Core interpretation; **timing** as seasons for consolidation; tension when pace demands differ.',
            ],
          },
          {
            h3: 'Number 5',
            p: [
              '**Core theme:** Change — honest contact with experience, variety, and the need to stay awake.',
              '**Strengths:** Adaptability, curiosity, courage to revise, sensory truth.',
              '**Challenges / shadow:** Restlessness, novelty chasing, scattered commitments, intensity mistaken for intimacy.',
              '**When dominant in a chart:** Movement, risk, and reinvention echo through cores or timing.',
              '**When missing or quiet:** Stability may dominate; growth edge is regulated variety and embodied risk.',
              '**How it interacts with other digits:** With **4** — freedom versus frame; with **6** — roaming versus roots; with **7** — outer stimulus versus inner sift.',
              '**Where it appears in reports:** Timing layers especially; core synthesis when life asks for flexibility against a fixed storyline.',
            ],
          },
          {
            h3: 'Number 6',
            p: [
              '**Core theme:** Care — responsibility to people and place; belonging with integrity.',
              '**Strengths:** Nurturance, repair, aesthetic harmony, loyalty that shows up in deeds.',
              '**Challenges / shadow:** Over-functioning, guilt, rescue patterns, resentment when care is not reciprocal.',
              '**When dominant in a chart:** Home, duty, and relational load recur as central questions.',
              '**When missing or quiet:** Cooler distance may be native; growth edge is bounded warmth, not self-erasure.',
              '**How it interacts with other digits:** With **5** — roots versus roads; with **8** — care versus command; with **9** — local love versus wide release.',
              '**Where it appears in reports:** Life-area sections on relationship or responsibility; tension when urge for freedom collides with duty.',
            ],
          },
          {
            h3: 'Number 7',
            p: [
              '**Core theme:** Depth — study, discernment, truth that survives solitude.',
              '**Strengths:** Analysis, pattern sense, integrity of inquiry, intuition trained by reflection.',
              '**Challenges / shadow:** Isolation, cynicism, analysis paralysis, using intellect to avoid vulnerability.',
              '**When dominant in a chart:** Inner life, standards, and meaning-making keep surfacing.',
              '**When missing or quiet:** Action may precede reflection; growth edge is deliberate pause without withdrawal.',
              '**How it interacts with other digits:** With **3** — silence versus speech; with **2** — head versus heart; with **8** — insight versus impact.',
              '**Where it appears in reports:** Core blocks; **patterns** when inner standards clash with outer demands; methodology-adjacent honesty in some readings.',
            ],
          },
          {
            h3: 'Number 8',
            p: [
              '**Core theme:** Power with consequence — resources, influence, and what you do when pressure arrives.',
              '**Strengths:** Executive stamina, fair leverage, building scale, endurance under load.',
              '**Challenges / shadow:** Control, collapse of rest, worth tied to outcomes, hardness as default armour.',
              '**When dominant in a chart:** Ambition and material or social weight recur across positions.',
              '**When missing or quiet:** Influence may feel foreign or feared; growth edge is proportional authority, not avoidance.',
              '**How it interacts with other digits:** With **2** — command versus consent; with **1** — personal drive versus systemic power; with **6** — outcomes versus care.',
              '**Where it appears in reports:** Core numbers and life areas touching work or boundaries; tension when soft and hard currencies disagree.',
            ],
          },
          {
            h3: 'Number 9',
            p: [
              '**Core theme:** Completion — perspective across cycles, compassion with edges, letting what is finished stay finished.',
              '**Strengths:** Generosity of view, mentoring range, humane closure, pattern recognition over time.',
              '**Challenges / shadow:** Over-carrying, sentimental stuckness, burnout, difficulty saying “enough.”',
              '**When dominant in a chart:** Endings, teaching, and wide-angle empathy keep returning.',
              '**When missing or quiet:** Present-moment focus may dominate; growth edge is honouring closure without drama.',
              '**How it interacts with other digits:** With **1** — release versus new start; with **4** — letting go versus maintaining; with **6** — universal care versus local duty.',
              '**Where it appears in reports:** Synthesis sections, life-area closure themes, timing as seasons of harvest or release.',
            ],
          },
        ],
      },
      {
        h2: 'Integration, not contradiction',
        p: [
          'Contrasts are normal. A **4** Life Path beside a **3** Expression is two valid languages: structure and air. Reading is **sequencing and boundaries** — which need leads in which season — not picking a winner.',
        ],
      },
      {
        h2: 'Reduction and masters (reminder)',
        p: [
          'Totals reduce to **1–9** unless that position’s rules preserve **11, 22, or 33** on the **final** step. See the **Master numbers** lesson for voltage and integration; intermediate elevens in scratch work are not automatically “yours.”',
        ],
      },
      {
        h2: 'Practice',
        p: [
          'Choose one digit from this page and one **other** digit. For each, write two sentences: how it would read as **Life Path** versus **Soul Urge**. Then add one sentence on where a report would most likely weave them together (core, patterns, tension, or timing).',
        ],
      },
      {
        h2: 'Next',
        p: [
          'Return to **Master numbers** if masters appear in your chart, then **secondary numbers** when core positions are stable — secondary layers sharpen, they do not replace core work.',
        ],
      },
    ],
    howInReport:
      'The engine maps your date and name into positions, then writes where digits **repeat**, **pull apart**, and **sequence** — summary, core blocks, patterns, tension, life areas, and timing. This lesson is the vocabulary those sections use; it is not a substitute for your personal synthesis.',
    ctaNote,
  },

  'master-numbers-11-22-33': {
    title: 'Master Numbers 11, 22, 33',
    section: 'about',
    seo: ['Life path number meaning'],
    intro:
      'Master numbers are not a separate mystical add-on bolted onto normal digits. They are 11, 22, or 33 left standing at the end of a calculation when the rules for that position say they may stand. They intensify the theme of the neighbouring single digit — 1 for 11, 4 for 22, 6 for 33 — while widening the stakes: more sensitivity, more reach, more responsibility to integrate the pattern without burning out. This lesson separates theatre from mechanics: what masters tend to feel like in general language, when they actually count in a chart, and how the rest of your numbers still matter.',
    blocks: [
      {
        h2: 'What changes when a master holds',
        p: [
          'Volume and consequence. The same thematic family as the adjacent digit — initiative, building, care — but with finer nerves and a bigger field of practice. Masters are not automatically “lucky”; they are often more work, more discernment, more need for boundaries.',
        ],
      },
      {
        h2: 'Eleven — intuition on the line of one',
        p: [
          'Nervous-system literacy: reading rooms before arguments arrive, feeling truth before language catches up. The growth edge is pairing that sensitivity with structure — sleep, body care, plain routines — so insight does not flood the system.',
        ],
      },
      {
        h2: 'Twenty-two — vision with scaffolding',
        p: [
          'The 4 theme amplified: building something that can bear weight. The strain is scale — wanting the cathedral on a weeknight. Mastery looks like sequencing, delegation, and saying no to heroic pacing.',
        ],
      },
      {
        h2: 'Thirty-three — compassion with accountability',
        p: [
          'The 6 theme widened: healing, teaching, holding space. The strain is martyrdom — love without limits becomes resentment. Boundaries protect both the work and the person doing it.',
        ],
      },
      {
        h2: 'When an eleven in your scratch work is not “your” master number',
        p: [
          'Charts preserve 11, 22, or 33 only at defined final steps for each position. An intermediate 11 while reducing a long sum is often just a landing pad on the way to 2. If your pencil disagrees with the engine, walk the Life Path or name lesson line by line — arithmetic errors masquerade as interpretation debates.',
        ],
      },
      {
        h2: 'Masters in context',
        p: [
          'A master in one corner of the chart does not erase friction elsewhere. Expression, Soul Urge, cycles, and birthday texture still qualify the story. Timing and layered synthesis belong in the personal report; earlier lessons stay with definition and general theme so interpretation stays grounded in method.',
        ],
      },
      {
        h2: 'Practice',
        p: [
          'On paper, take a subtotal of 29 (2 + 9 = 11) and explain whether it is “a master” depends on **which position** and **which step** — not on seeing 11 anywhere in scratch work. Then list one integration habit for 11, one for 22, one for 33 in plain language (sleep, sequencing, boundaries).',
        ],
      },
      {
        h2: 'Next',
        p: [
          'Return to **Life Path** or **name calculations** if you are finishing a chart; continue module order toward **secondary numbers** once core positions are stable.',
        ],
      },
    ],
    howInReport:
      'When a master appears in your chart, the report names it in plain language — strengths, strain, and integration — alongside your other numbers, not as an isolated halo.',
    ctaNote,
  },

  'appendix-angel-numbers': {
    title: 'Appendix: Angel numbers (optional)',
    section: 'about',
    seo: ['How numerology works'],
    intro:
      'Optional lens only — not part of chart calculation or the structured course path. Angel numbers are repeating sequences people notice in clocks, receipts, and daily life. Useful as a mindfulness hook, not as a second numerology system.',
    blocks: [
      {
        h2: 'Attention without a fixed dictionary',
        p: [
          'Repeating digits can act as reminders to breathe, refocus, or return to what you already decided matters.',
          'If a sequence keeps appearing, track context: what were you avoiding, rushing, or needing to hear? That inquiry usually teaches more than memorising meanings for 111 or 444.',
        ],
      },
    ],
    howInReport:
      'Your formal chart uses structured calculations from birth data and name; angel-number noticing is an optional, personal layer you can bring alongside your report.',
    ctaNote,
  },

  'life-path-number-calculator': {
    title: 'Life Path number',
    section: 'chart',
    seo: ['Life path number meaning'],
    intro:
      'The Life Path is the spine of most charts: one number distilled from your full birth date, read as a single digit or as 11, 22, or 33 when the final reduction allows. It is not a career prescription and not a moral score. It is a slow story — what kinds of lessons return, what pace of growth feels native, what kind of resistance keeps asking for skill. This lesson covers calculation, checking your work, and how to read the Life Path as a long arc — not as a personality label. Personal synthesis across your whole chart stays in the report.',
    blocks: [
      {
        h2: 'What you are actually calculating',
        p: [
          'You are reducing the month, the day, and the birth year into comparable pieces, combining them, then reducing again until the Life Path total resolves — either to 1–9 or, at the last step, to 11, 22, or 33 if your system preserves masters there.',
          'Along the way you will see elevens, twenty-twos, or thirty-threes in intermediate sums. Those are not automatically “your master number” for the chart. Only the rule for the final Life Path position decides what stands.',
        ],
      },
      {
        h2: 'Calculation — step by step',
        ol: [
          'Write the birth date as month, day, and full year. Do not mix systems — use one rule for November (usually 11, then reduce in the month slot per your engine).',
          'Reduce the month to one digit (or master allowed for this step).',
          'Reduce the day: if the day is 11 or 22, keep as two-digit master at this stage when your rules specify; otherwise add digits until one digit remains.',
          'Reduce the year: sum all digits of the birth year; repeat until the year component is a single digit or master per your rules.',
          'Add the three reduced parts (month + day + year). Reduce that sum until it becomes 1–9, or stop at 11, 22, or 33 if that total is allowed as the final Life Path in your system.',
        ],
      },
      {
        h2: 'Why month, day, and year are handled separately first',
        p: [
          'Breaking the date into three reduced components before the last sum keeps the work legible on paper and matches how most engines implement the rule. It also makes errors obvious — transposed digits in the birth year are the classic failure mode.',
          'If you insist on adding every digit of the date in one long line, you may still reach the same endpoint, but you lose the checkpoints that make hand calculation trustworthy.',
        ],
      },
      {
        h2: 'Worked example you can copy',
        p: [
          '14 July 1986: month 7; day 14 → 1 + 4 = 5; year 1986 → 1 + 9 + 8 + 6 = 24 → 2 + 4 = 6. Sum: 7 + 5 + 6 = 18 → 1 + 8 = 9. Life Path 9.',
          'Run it twice: once in a single line for the year, once digit by digit, to see that the year reduction is where mistakes usually hide.',
        ],
      },
      {
        h2: 'Checks and common mistakes',
        p: [
          'Calling every intermediate 11 a master Life Path. Preservation is about the final step for that position, not every eleven in the scratch pad.',
          'Stopping at a keyword (“I’m a 7”) and never asking how Expression, Soul Urge, or the current year complicate that story.',
          'Expecting a single lesson to narrate your chart. The method is here; weaving your numbers together is the next modules or the report.',
        ],
      },
      {
        h2: 'How to read the Life Path without shrinking yourself',
        p: [
          'Read for recurring themes, not for a verdict. A Life Path describes pacing and pressure over time: what kind of growth keeps knocking, what skills mature slowly, what resistance feels familiar in more than one decade.',
          'Expression tells you how you tend to perform; Soul Urge tells you what you crave; timing tells you which season you are in. None of those erase the Life Path — they colour it. If you treat the Life Path as a single identity label, you will feel both over-exposed and unseen.',
        ],
      },
      {
        h2: 'Life Paths 1–9 — what each digit tends to ask',
        p: [
          'These lines are deliberately short; the Numbers 1–9 lesson deepens the vocabulary. Here the focus is the Life Path job: the long lesson each digit keeps returning to.',
        ],
        ul: [
          '1 — Standing in your own initiative: starting without waiting for permission; learning that leadership includes repair, not only launch.',
          '2 — Harmony under real conditions: sensitivity, diplomacy, honest needs; learning that peace is not the same as absence of conflict.',
          '3 — Giving shape to what wants to be said: creativity, connection, expression; learning that visibility needs containers.',
          '4 — Building what lasts: discipline, systems, endurance; learning that structure is not the enemy of soul.',
          '5 — Staying honest with change: freedom, experience, adaptation; learning that variety without discernment is just noise.',
          '6 — Responsibility with heart: care, belonging, repair; learning that love needs boundaries.',
          '7 — Depth over noise: inquiry, solitude, discernment; learning that trust is earned, not performed.',
          '8 — Power with integrity: impact, resources, consequence; learning that influence without rest hollows the person.',
          '9 — Perspective and release: completion, compassion, letting go; learning that closure is not cruelty.',
        ],
      },
      {
        h2: 'Master Life Paths: 11, 22, and 33',
        p: [
          'When the final Life Path total resolves to a master number, the theme widens: more sensitivity, more scale, more obligation to integration. Eleven leans on intuition and nerve; twenty-two on building something durable from vision; thirty-three on compassion with accountability.',
          'The adjacent single digit still matters: read it as the floor under the master — the place you return to when the voltage of the master feels unmanageable.',
        ],
      },
      {
        h2: 'Why the Life Path is only the headline',
        p: [
          'Knowing the digit without context is like knowing the genre of a novel without reading the chapters. The report exists to write the chapters — your name, your birthday texture, your current cycle — without turning lessons into a substitute for integrated interpretation.',
        ],
      },
      {
        h2: 'Practice',
        p: [
          'Compute the Life Path twice on paper for one birth date: (1) using separate month, day, year reductions as in this lesson; (2) adding all date digits in one line. If the results differ, find which step diverged. Write two sentences interpreting the final digit as a long-arc theme only — no other positions yet.',
        ],
      },
      {
        h2: 'Next',
        p: [
          'If your final Life Path might be 11, 22, or 33, read **Master Life Paths** next; otherwise continue to **Expression** and the other name-number lessons.',
        ],
      },
    ],
    howInReport:
      'The report recomputes your Life Path with these rules, then writes it as part of a larger portrait: how the long arc meets your name, your birthday emphasis, and the year you are in.',
    ctaNote,
  },

  'your-core-numbers': {
    title: 'Your Core Numbers',
    section: 'chart',
    seo: ['Numerology chart meaning'],
    intro:
      '“Core numbers” are the main lenses most charts lean on: the long arc of the birth date, the three angles of the birth name, and the sharper texture of the birth day. None of them wins a fight for “the real you.” They answer different questions — pace versus motivation versus surface style — and the interesting material is usually in how they negotiate. This lesson names what each position is for, how they are built, and why contrast inside a chart is a feature, not a bug.',
    blocks: [
      {
        h2: 'The positions and what each one is actually doing',
        p: [
          'Life Path — derived from the full birth date. It is the slow story: recurring chapters, long lessons, the undertow beneath reinvention.',
          'Expression — derived from every letter of the birth name. It is capacity and style in motion: how work, speech, and responsibility tend to show when you are resourced.',
          'Soul Urge — vowels only. It is appetite and private truth: what you reach toward when nobody is grading you.',
          'Personality — consonants only. It is the first pass: pace, guard, what people read before they know you.',
          'Birthday — the day of birth, reduced. It is a closer hand than the Life Path: talents, sensitivities, and small-loop themes that show up in day-to-day life.',
        ],
      },
      {
        h2: 'Why contrast is the point',
        p: [
          'Charts rarely sing one note. A steady, builder Life Path can sit beside a lively Expression — structure beneath a life that looks expressive from the outside. The tension is not hypocrisy; it is logistics for the soul: which need leads in which season, and what kind of boundaries make both legitimate.',
          'The course teaches you to recognise those patterns in general. The report writes the synthesis in your voice.',
        ],
      },
      {
        h2: 'How each position is calculated (same engine, different input)',
        p: [
          'Date-based positions sum and reduce parts of the birth date. Name-based positions map A–Z to digits with one table, then sum different subsets of letters. The reduction habit is shared; the pool of digits is not.',
        ],
        ul: [
          'Life Path — full month, day, and year, then final reduction.',
          'Expression — all letters of the full birth name.',
          'Soul Urge — vowels only (Y by rule — see the name lesson).',
          'Personality — consonants; Y follows vowel/consonant logic.',
          'Birthday — day of month, then reduced (masters where rules allow).',
        ],
      },
      {
        h2: 'Terrain and weather',
        p: [
          'Core numbers are terrain: the landscape you keep crossing. Cycles and personal years are weather: what this season emphasises. The personal report stacks weather on terrain without pretending the terrain is gone.',
        ],
      },
      {
        h2: 'Personality number as presentation',
        p: [
          'Personality (consonants) describes stable presentation patterns — what people tend to notice first — not a forecast. It pairs with Expression and Soul Urge: forecasts emphasise timing; the consonant lens emphasises surface style under ordinary pressure.',
        ],
      },
      {
        h2: 'Common mistakes',
        ul: [
          'Treating Expression as “the real you” and ignoring Life Path pacing.',
          'Reading Soul Urge and Personality in isolation — they are one conversation.',
          'Skipping Birthday because it feels small — it often explains day-to-day friction.',
        ],
      },
      {
        h2: 'Practice',
        p: [
          'List the five positions in order: Life Path, Expression, Soul Urge, Personality, Birthday. Beside each, write one phrase: “This answers the question: ___” without calculating yet. Then run one real or sample chart and check whether your phrases still fit.',
        ],
      },
      {
        h2: 'Next',
        p: [
          'Follow the course order: **Life Path** calculation, then **Master Life Paths**, then each **name number** lesson, then **Birthday** (if not already done) — each deepens one core lens.',
        ],
      },
    ],
    howInReport:
      'Core positions are ordered into one narrative: how your arc, name, and birthday interlock, with deeper synthesis and timing in the personal report.',
    ctaNote,
  },

  'your-birth-day-number': {
    title: 'Your Birth Day Number',
    section: 'chart',
    seo: ['Numerology chart meaning'],
    intro:
      'The Birthday number is the reduced day of birth. It adds texture: a more specific “hand you were dealt” tone alongside the wider Life Path.',
    blocks: [
      {
        h2: 'Core idea',
        p: [
          'Only the calendar day of birth is used — not the month or year. That single component is reduced to 1–9 or, when your rules allow at this final step, to 11 or 22. It is a close-up lens: talents, irritations, and daily habits that sit under the longer Life Path story.',
        ],
      },
      {
        h2: 'Calculation',
        ol: [
          'Write the day of birth as a number (1–31).',
          'If the day is 11 or 22, either preserve it as a master for this position or reduce per your engine’s rule set (many systems reduce 11 → 2 and 22 → 4 for the birthday position — match your Life Path engine).',
          'For any other two-digit day, add the digits and repeat until you have one digit (or an allowed master). Example: day 28 → 2 + 8 = 10 → 1 + 0 = 1.',
        ],
      },
      {
        h2: 'Worked example',
        p: [
          'Day 14 → 1 + 4 = 5. Day 9 → 9. Day 11 → keep 11 or reduce to 2 per your rules — state which rule you used when you write the reading.',
        ],
      },
      {
        h2: 'Interpretation',
        p: [
          'Read the result with the same 1–9 vocabulary as other positions, but keep scale in mind: birthday describes recurring “small loop” themes — how you meet a Tuesday, a first impression in a room, a skill that feels native — not the whole life plot.',
        ],
      },
      {
        h2: 'In the wider chart',
        p: [
          'Pair birthday with Life Path: a steady Life Path 4 with a lively birthday digit can show structure in the long arc with more spontaneity in close-up situations. Name numbers still qualify tone; timing still seasons urgency.',
        ],
      },
      {
        h2: 'Common mistakes',
        ul: [
          'Accidentally summing month + day + year when the lesson only asks for the day.',
          'Calling a birthday master without checking whether your software preserves masters for this position.',
          'Letting the birthday overrule the Life Path in narrative — it modifies, it does not replace.',
        ],
      },
      {
        h2: 'Practice',
        p: [
          'Compute the birthday digit for three dates you know well (friends or public figures). Write one line each on how the digit might show in daily style versus their Life Path story.',
        ],
      },
      {
        h2: 'Next',
        p: [
          '**Name numerology** shows how the full birth name produces Expression, Soul Urge, and Personality — three angles from the same letters.',
        ],
      },
    ],
    howInReport:
      'Birthday emphasis appears alongside core synthesis so you can connect macro arc with micro style.',
    ctaNote,
  },

  'your-name-numerology': {
    title: 'Your Name Numerology',
    section: 'chart',
    seo: ['Name numerology meaning'],
    intro:
      'Your birth name is not one number — it is three coherent readings built from the same letters. Expression uses every letter; Soul Urge uses vowels; Personality uses consonants. Same string, different filters: outer voice, inner appetite, and the surface people meet first. Once you understand that split, name numerology stops sounding like mystifying word-math and becomes something you can check on paper with the same table the engine uses.',
    blocks: [
      {
        h2: 'Why one name produces three numbers',
        p: [
          'Letters carry frequency. Vowels lean toward what you want and what you feel; consonants lean toward how you strike the ear and how you guard yourself; the full string describes the whole instrument. None of the three cancels the others — they argue, align, and qualify each other.',
        ],
      },
      {
        h2: 'Letter values and reduction',
        p: [
          'Use the Pythagorean map: A–I = 1–9, then J–R, then S–Z. Drop spaces and punctuation. Sum the subset you need, then reduce until you reach a single digit or a master allowed at the final step for that position.',
        ],
      },
      {
        h2: 'Soul Urge (Heart’s Desire)',
        p: [
          'Isolate vowels A, E, I, O, U in each name part. Y is the usual complication: our engine applies one consistent rule so your hand math can match the software — Y is treated as a vowel when it functions as one for the syllable (for example when it carries a vowel sound, appears at the end of a name part, or stands without another vowel beside it).',
          'Sum vowel values, then reduce. If your total disagrees with the engine, re-check Y before you doubt everything else.',
        ],
      },
      {
        h2: 'Personality — the consonant veil',
        p: [
          'Sum consonants: every letter not counted as a vowel in that position, including Y when it behaves as a consonant. This is the “first glance” number — partial by design, not because it is false, but because it is only one camera angle.',
        ],
      },
      {
        h2: 'Expression — the whole name',
        p: [
          'Sum every letter. This is the fullest picture of how the name moves through the world: work, speech, signature style. It is the number people often confuse with “who I am,” but it is still a theme — not a cage.',
          'Where Soul Urge isolates vowels and Personality isolates consonants, Expression uses every letter. The digit borrows the 1–9 vocabulary from the Number meanings lesson — but Life Path is the long arc; Expression is how you tend to operate in the world. Same digit, different office.',
        ],
      },
      {
        h2: 'Expression calculation in detail',
        p: [
          'Remove anything that is not A–Z. Map each letter to its Pythagorean value. Sum all letters in the full birth name as it is used for the chart. Reduce by adding digits until you land on 1–9 or on an allowed master at the final step.',
        ],
      },
      {
        h2: 'A small example on paper',
        p: [
          'ANNA → 1 + 5 + 5 + 1 = 12 → 1 + 2 = 3. Longer names are the same process with more rows; the only discipline is one letter per line if you need to stay honest.',
        ],
      },
      {
        h2: 'Which spelling to use',
        p: [
          'Charts usually begin from the birth certificate spelling because it is the name you first carried in public record. If you use a different stable spelling in daily life, advanced work can explore that — but the engine defaults to one string so comparisons stay consistent.',
        ],
      },
      {
        h2: 'Reading the three as a conversation',
        p: [
          'When Soul Urge and Personality align, inner life and surface line up. When they diverge, you get the familiar split between what you crave and what you show — useful context for work and intimacy, not a moral failure. Work the three numbers on paper in one sitting so you are not flipping between lessons under pressure; the report writes the synthesis.',
        ],
      },
      {
        h2: 'Birth name changes',
        p: [
          'Legal and lived names can be explored in advanced practice. For a single coherent chart, we anchor on the birth name unless you deliberately choose otherwise — that keeps comparisons honest.',
        ],
      },
      {
        h2: 'Practice',
        p: [
          'On one birth name string, compute Soul Urge (vowels only), Personality (consonants), and Expression (all letters). Write one paragraph describing where inner appetite and outer surface agree or diverge. Re-check Y handling if any total disagrees with your engine.',
        ],
      },
      {
        h2: 'Next',
        p: [
          'With core numbers in place, move to **number meanings** for vocabulary depth, then **secondary** and **timing** lessons as the course outlines.',
        ],
      },
    ],
    howInReport:
      'Name-based numbers appear with integrated interpretation — how vowels, consonants, and the full name interlock — rather than three disconnected definitions.',
    ctaNote,
  },

  'how-to-read-your-numerology-chart': {
    title: 'How to read a chart',
    section: 'chart',
    seo: ['Numerology chart meaning'],
    intro:
      'A chart is not a list of labels. It is a set of themes that cooperate, contradict, and qualify each other. This lesson gives a practical reading order and language for tension, harmony, and comparison — so you can move from “I have a 5” to “how my 5 negotiates with the rest of my pattern.”',
    blocks: [
      {
        h2: 'A sensible reading order',
        p: [
          'Start with Life Path (long arc). Then name numbers — Expression, Soul Urge, Personality — as one conversation. Add Birthday for close-up texture. Bring in Personal Year and finer timing after the core terrain is clear.',
          'Skipping straight to timing is like reading weather without knowing the landscape you are standing on.',
        ],
      },
      {
        h2: 'When the chart argues with itself',
        p: [
          'Contrasts are normal. A 4 Life Path beside a 3 Expression is not “hypocrisy” — it is two legitimate languages: one wants structure and duration; the other wants air and audience. The work is integration: sequencing, honesty about which need is primary in which season, and boundaries that protect both.',
          'Reading contrasts well is what separates chart literacy from label shopping. The lessons give you the pieces; the report holds your actual combination without flattening it.',
        ],
      },
      {
        h2: 'Compatibility between charts',
        p: [
          'Comparing charts can show where two people naturally align (shared rhythm, similar needs) and where differences ask for skill (one person’s need for space beside another’s need for reassurance).',
          'Use compatibility insights as prompts for conversation and boundaries — not as fixed scores.',
        ],
      },
    ],
    howInReport:
      'When you generate a report for yourself, you receive a grounded profile you can compare thoughtfully with a partner’s profile — same engine, same clarity.',
    ctaNote,
  },

  'numerology-timing-cycles': {
    title: 'Timing cycles: Personal Year, Month, and Day',
    section: 'forecasts',
    seo: ['Personal year numerology'],
    intro:
      'Timing is layered: year, month, and day. Your core chart is the terrain; cycles are the weather. None of this replaces the Life Path or name numbers — it colours seasons and pacing. Choose a calendar year in focus when you want to study Personal Year; the logic is the same every year.',
    blocks: [
      {
        h2: 'Core idea',
        p: [
          'Personal Year, Month, and Day reuse your birth month and birth day with a calendar period you choose. Each layer reduces to 1–9 (or masters where your rules allow) and is read with the same digit vocabulary — but the time horizon shrinks from year to month to day.',
        ],
      },
      {
        h2: 'Personal Year — calculation',
        ol: [
          'Reduce your birth month to a single digit (November is often 11 then reduced to 2 — follow the same month rule you use for Life Path).',
          'Reduce your birth day to a single digit (11 or 22 as birth day may be preserved or reduced per your engine).',
          'Reduce the four digits of the calendar year in focus (e.g. 2026 → 2 + 0 + 2 + 6 = 10 → 1).',
          'Add the three reduced numbers (month + day + calendar year). Reduce the sum to 1–9 or stop at 11 or 22 if your system allows masters for Personal Year.',
        ],
      },
      {
        h2: 'Personal Year — worked example',
        p: [
          'Birth 14 July (month 7, day 14 → 5), calendar year 2026 → 1. Sum: 7 + 5 + 1 = 13 → 1 + 3 = 4. Personal Year 4 for 2026 for that birth date — read as a consolidation / structure season on top of the core chart.',
        ],
      },
      {
        h2: 'Personal Month — calculation',
        ol: [
          'Take the Personal Year number you already computed for the calendar year.',
          'Reduce the calendar month in focus (March = 3, October = 10 → 1).',
          'Add Personal Year + calendar month digit. Reduce to 1–9 (or allowed master).',
        ],
      },
      {
        h2: 'Personal Day — calculation',
        ol: [
          'Take the Personal Month number for that calendar month.',
          'Reduce the calendar day of the month (e.g. 24 → 2 + 4 = 6).',
          'Add Personal Month + calendar day digit. Reduce to 1–9 (or allowed master).',
        ],
      },
      {
        h2: 'Interpretation',
        p: [
          'Year = headline season; month = chapter inside that season; day = light dial for pacing tasks. Lead with core chart, then say how the year tints decisions — use month/day to sequence, not to rewrite identity.',
        ],
      },
      {
        h2: 'In the wider chart',
        p: [
          'A heavy Personal Year does not erase a reflective Life Path — it asks how that year’s theme meets the person’s native terrain. Name numbers describe how they will express or resist the year’s pressure.',
        ],
      },
      {
        h2: 'Common mistakes',
        ul: [
          'Using the wrong calendar year or forgetting to reduce the year digits first.',
          'Announcing Personal Day outcomes as fate — keep language optional and gentle.',
          'Discussing timing before the learner knows their core numbers — stack terrain, then weather.',
        ],
      },
      {
        h2: 'Practice',
        p: [
          'Pick your own birth date and compute Personal Year for the current calendar year. Then compute Personal Month for this month and Personal Day for today. Write one sentence each linking the digit to a practical pacing choice (rest, outreach, focus, etc.).',
        ],
      },
      {
        h2: 'Next',
        p: [
          'The dedicated **Personal Year**, **Personal Month**, and **Personal Day** lessons repeat the formulas with more reading notes; **How to interpret timing periods** shows how to stack cycles without contradiction.',
        ],
      },
    ],
    howInReport:
      'The report connects Personal Year (and finer timing where relevant) to your full chart as context — not command.',
    ctaNote,
  },

  'your-personal-numerology-report': {
    title: 'Your personal report',
    section: 'readings',
    seo: ['Numerology chart meaning'],
    intro:
      'This lesson describes the full written report you generate in this space: one synthesis from your birth data — core numbers, how they interact, life themes, strengths and challenges, current cycle as context, and practical insight.',
    blocks: [
      {
        h2: 'One integrated reading',
        p: [
          'The report is not a separate “tier” of product. It is one interpretation that uses the same rules you learned in the course: the same reductions, the same positions, and the same habit of reading themes in relation to each other.',
        ],
      },
      {
        h2: 'What you are looking at',
        p: [
          'Expect synthesis across Life Path, name numbers, birthday emphasis, and timing — consonant-driven tone and cross-checks between name numbers and life areas appear where they help your chart, not as a separate gimmick.',
        ],
      },
    ],
    howInReport:
      'The report generator produces one complete written interpretation from your birth data.',
    ctaNote,
  },

  ...NUMEROLOGY_COURSE_EXTRA_ARTICLES,
};

export {
  NUMEROLOGY_COURSE,
  NUMEROLOGY_COURSE_APPENDIX,
  MODULE_PATHS,
  NUMEROLOGY_COURSE_MODULES,
} from './numerologyCourse';

/** @deprecated Use NUMEROLOGY_COURSE */
export const NUMEROLOGY_LEARN_GROUPS = NUMEROLOGY_COURSE.map((m) => ({
  id: m.id,
  title: `Module ${m.number} — ${m.title}`,
  description: m.description,
  links: m.lessons.filter((l) => l.slug).map((l) => ({ slug: l.slug, label: l.label })),
}));

/** Retained for compatibility */
export const NUMEROLOGY_REPORT_EXPLAINERS = [{ slug: 'your-personal-numerology-report', label: 'Your personal report' }];
